/**
 * Wave 1 — Shared billing types mirroring the operations Prisma enums.
 *
 * Source of truth: apps/operations/prisma/schema.prisma (the enums introduced
 * by the Wave 1 invoicing-receipts foundation migration).
 *
 * Conventions follow the rest of `lib/api/types`:
 * `as const` object + derived union via `(typeof X)[keyof typeof X]`.
 *
 * Spec reference:
 *   docs/requirements/Notifi_V2_Invoicing_Receipts_Spec_v1.0.md §2/§4/§5/§6/§8/§9.
 *
 * NOTE: invoice and payment status strings are uppercase because that's the
 * shape the new operations API returns. The legacy lowercase `InvoiceStatus`
 * union under `apps/notifi-frontend/src/types/invoice.ts` is being phased out;
 * see PR 2 of the invoicing-receipts plan for the UI migration.
 */

// ─── Invoice ───────────────────────────────────────────────────────────────

/**
 * Spec §2.2 — Invoice lifecycle. Legacy `'SENT'`, `'VIEWED'`, and `'OVERDUE'`
 * collapse to `OPEN`; viewing is now tracked via `Invoice.viewedAt`, overdue
 * is computed from `dueDate < now()`.
 */
export const InvoiceStatus = {
  DRAFT: 'DRAFT',
  OPEN: 'OPEN',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  VOID: 'VOID',
  WRITTEN_OFF: 'WRITTEN_OFF',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

// ─── Payment ───────────────────────────────────────────────────────────────

/**
 * Spec §6.1 — Payment processing states.
 *
 * DEVIATION: spec lists 6 states; the operations service retains the V1
 * dispute substates (`DISPUTE_UNDER_REVIEW`, `CHARGEBACK_LOST`,
 * `CHARGEBACK_WON`) as additional values driven by gateway webhooks.
 * Mirrored here so frontend code can render them; a follow-up may move
 * these onto a separate `disputeState` field per the spec.
 */
export const PaymentStatus = {
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  REVERSED: 'REVERSED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  DISPUTED: 'DISPUTED',
  DISPUTE_UNDER_REVIEW: 'DISPUTE_UNDER_REVIEW',
  CHARGEBACK_LOST: 'CHARGEBACK_LOST',
  CHARGEBACK_WON: 'CHARGEBACK_WON',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

// ─── Credit Memo ───────────────────────────────────────────────────────────

/** Spec §8.1 — CreditMemo type discriminator. Drives QBO GL routing. */
export const CreditMemoType = {
  REFUND: 'REFUND',
  WRITE_OFF: 'WRITE_OFF',
  GOODWILL: 'GOODWILL',
  ADJUSTMENT: 'ADJUSTMENT',
  DEPOSIT_REFUND: 'DEPOSIT_REFUND',
  OVERPAYMENT: 'OVERPAYMENT',
} as const;
export type CreditMemoType = (typeof CreditMemoType)[keyof typeof CreditMemoType];

/**
 * Spec §8 — CreditMemo lifecycle. The backend now persists this as the
 * `CreditMemoStatus` Postgres enum; this constant mirrors the values
 * for FE-side narrowing (TypeScript can't reach into Prisma's runtime
 * enum from the frontend bundle).
 */
export const CreditMemoStatus = {
  OPEN: 'OPEN',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPLIED: 'APPLIED',
  VOID: 'VOID',
} as const;
export type CreditMemoStatus = (typeof CreditMemoStatus)[keyof typeof CreditMemoStatus];

// ─── Deposit ───────────────────────────────────────────────────────────────

/** Spec §4.1 — Deposit category. */
export const DepositType = {
  PROPOSAL: 'PROPOSAL',
  JOB: 'JOB',
  PROGRESS: 'PROGRESS',
  AD_HOC: 'AD_HOC',
} as const;
export type DepositType = (typeof DepositType)[keyof typeof DepositType];

/** Spec §4.2 — Deposit lifecycle state. */
export const DepositStatus = {
  CAPTURE_PENDING: 'CAPTURE_PENDING',
  CAPTURE_FAILED: 'CAPTURE_FAILED',
  HELD: 'HELD',
  PARTIALLY_APPLIED: 'PARTIALLY_APPLIED',
  APPLIED: 'APPLIED',
  REFUND_PENDING: 'REFUND_PENDING',
  REFUNDED: 'REFUNDED',
  FORFEITED: 'FORFEITED',
} as const;
export type DepositStatus = (typeof DepositStatus)[keyof typeof DepositStatus];

// ─── Receipt ───────────────────────────────────────────────────────────────

/** Spec §6.3 — Receipt artifact kind. */
export const ReceiptKind = {
  PAYMENT: 'PAYMENT',
  DEPOSIT: 'DEPOSIT',
} as const;
export type ReceiptKind = (typeof ReceiptKind)[keyof typeof ReceiptKind];

// ─── Payment Schedule ──────────────────────────────────────────────────────

/** Spec §5.1 — Payment schedule lifecycle. */
export const PaymentScheduleStatus = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  COMPLETED: 'COMPLETED',
} as const;
export type PaymentScheduleStatus =
  (typeof PaymentScheduleStatus)[keyof typeof PaymentScheduleStatus];

/** Spec §5.2 — Triggers that fire a schedule rule. */
export const PaymentTrigger = {
  ON_ESTIMATE_ACCEPTED: 'ON_ESTIMATE_ACCEPTED',
  ON_JOB_SCHEDULED: 'ON_JOB_SCHEDULED',
  ON_JOB_STARTED: 'ON_JOB_STARTED',
  ON_MILESTONE_COMPLETE: 'ON_MILESTONE_COMPLETE',
  ON_JOB_COMPLETED: 'ON_JOB_COMPLETED',
  DAYS_AFTER_COMPLETION: 'DAYS_AFTER_COMPLETION',
  FIXED_DATE: 'FIXED_DATE',
} as const;
export type PaymentTrigger = (typeof PaymentTrigger)[keyof typeof PaymentTrigger];

/** Spec §5.4 — Action a fired rule performs. */
export const PaymentAction = {
  CREATE_DEPOSIT_INVOICE: 'CREATE_DEPOSIT_INVOICE',
  CREATE_INVOICE: 'CREATE_INVOICE',
  AUTO_CHARGE: 'AUTO_CHARGE',
  REQUEST_PAYMENT_ONLY: 'REQUEST_PAYMENT_ONLY',
} as const;
export type PaymentAction = (typeof PaymentAction)[keyof typeof PaymentAction];

// ─── Consent ───────────────────────────────────────────────────────────────

/** Spec §5.5 — Where the consent was captured. */
export const ConsentCaptureSource = {
  ESTIMATE_ACCEPTANCE: 'ESTIMATE_ACCEPTANCE',
  SAVE_CARD_ON_FILE: 'SAVE_CARD_ON_FILE',
  SETTINGS_PAGE: 'SETTINGS_PAGE',
} as const;
export type ConsentCaptureSource = (typeof ConsentCaptureSource)[keyof typeof ConsentCaptureSource];

// ─── QBO Sync ──────────────────────────────────────────────────────────────

/** Spec §9.1 / §9.7 — One-to-one QBO mirror state. */
export const SyncState = {
  PENDING: 'PENDING',
  SYNCED: 'SYNCED',
  FAILED: 'FAILED',
  DEAD_LETTER: 'DEAD_LETTER',
} as const;
export type SyncState = (typeof SyncState)[keyof typeof SyncState];

// ─── Payment method health ─────────────────────────────────────────────────

/** Spec §6.4 / §7.3 / §7.5 — Decline classification for a stored method. */
export const PaymentMethodDeclineState = {
  HEALTHY: 'HEALTHY',
  SOFT_DECLINED: 'SOFT_DECLINED',
  HARD_DECLINED: 'HARD_DECLINED',
  INVALID: 'INVALID',
} as const;
export type PaymentMethodDeclineState =
  (typeof PaymentMethodDeclineState)[keyof typeof PaymentMethodDeclineState];
